import { Component, EventEmitter, Output, AfterViewInit, ElementRef, QueryList, ViewChildren, ChangeDetectorRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NovaApiService, GetSampleQuestionsRequest } from '../../../services/api-services/nova-api.service';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-nova-suggestion',
  standalone: true,
  imports: [CommonModule, MatTooltipModule],
  templateUrl: './nova-suggestion.component.html',
  styleUrl: './nova-suggestion.component.scss'
})
export class NovaSuggestionComponent implements OnInit, AfterViewInit {
  @Output() suggestionSelected = new EventEmitter<string>();
  @ViewChildren('cardTitle') cardTitles!: QueryList<ElementRef>;

  // Track which cards have truncated text
  truncatedCards: Set<number> = new Set<number>();

  suggestions: Array<{
    id: number;
    title: string;
    icon?: string;
    alt?: string;
  }> = [];

  private readonly MAX_QUESTIONS = 6;

  // Get grid columns class based on number of suggestions
  get gridColumnsClass(): string {
    const count = this.suggestions.length;
    if (count >= 6) {
      return 'grid-3-col';
    } else if (count === 5) {
      return 'grid-3-col grid-5-items'; // 3 columns with special handling for 5 items
    } else if (count === 4) {
      return 'grid-2-col';
    } else if (count === 3) {
      return 'grid-3-col';
    } else if (count === 2) {
      return 'grid-2-col';
    } else {
      return 'grid-1-col';
    }
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private novaApiService: NovaApiService
  ) {}

  ngOnInit() {
    this.loadSampleQuestions();
  }

  loadSampleQuestions() {
    // Check if NOVA_APPLICATION_ID is configured
    if (!environment.NOVA_APPLICATION_ID) {
      console.error('NOVA_APPLICATION_ID is not configured in environment variables');
      this.suggestions = [];
      return;
    }

    const request: GetSampleQuestionsRequest = {
      application_id: environment.NOVA_APPLICATION_ID
    };

    this.novaApiService.getSampleQuestions(request).subscribe({
      next: (response) => {
        if (response.success && response.data.sample_questions) {
          // Convert API response to suggestions format, limit to top 6 questions
          this.suggestions = response.data.sample_questions
            .slice(0, this.MAX_QUESTIONS)
            .map((question, index) => ({
              id: index + 1,
              title: question
            }));
          this.cdr.detectChanges();
          // Check for truncation after suggestions are loaded
          setTimeout(() => {
            this.checkTextTruncation();
          }, 0);
        } else {
          // Fallback to empty array or default suggestions if API fails
          // console.warn('Failed to load sample questions:', response.message);
          this.suggestions = [];
        }
      },
      error: (error) => {
        console.error('Error loading sample questions:', error);
        // Fallback to empty array if API call fails
        this.suggestions = [];
      }
    });
  }

  ngAfterViewInit() {
    // Check for text truncation after view is initialized
    setTimeout(() => {
      this.checkTextTruncation();
    }, 0); // Use a shorter timeout

    // Also check after a longer delay to catch any layout changes
    setTimeout(() => {
      this.checkTextTruncation();
    }, 300);
  }

  checkTextTruncation() {
    if (this.cardTitles && this.cardTitles.length > 0) {
      this.cardTitles.forEach((titleRef, index) => {
        const element = titleRef.nativeElement;

        // Check if element has overflow (scrollHeight > clientHeight)
        // This is the most reliable way to detect truncation with -webkit-line-clamp
        const hasOverflow = element.scrollHeight > element.clientHeight;

        // Also create a temporary element to double-check
        const tempElement = document.createElement('div');
        const computedStyle = window.getComputedStyle(element);

        // Copy all relevant styles
        tempElement.style.position = 'absolute';
        tempElement.style.visibility = 'hidden';
        tempElement.style.height = 'auto';
        tempElement.style.maxHeight = 'none';
        tempElement.style.webkitLineClamp = 'none';
        tempElement.style.display = 'block';
        tempElement.style.width = element.offsetWidth + 'px';
        tempElement.style.padding = computedStyle.padding;
        tempElement.style.margin = computedStyle.margin;
        tempElement.style.fontFamily = computedStyle.fontFamily;
        tempElement.style.fontSize = computedStyle.fontSize;
        tempElement.style.fontWeight = computedStyle.fontWeight;
        tempElement.style.lineHeight = computedStyle.lineHeight;
        tempElement.style.letterSpacing = computedStyle.letterSpacing;
        tempElement.style.boxSizing = computedStyle.boxSizing;

        // Set the text content
        tempElement.textContent = element.textContent || '';

        document.body.appendChild(tempElement);
        const fullHeight = tempElement.scrollHeight;
        const actualHeight = element.clientHeight;
        document.body.removeChild(tempElement);

        // Text is truncated if either method detects overflow
        const isTruncated = hasOverflow || (fullHeight > actualHeight + 1); // +1 for rounding errors

        // Track truncated state for this card
        if (isTruncated) {
          this.truncatedCards.add(index);
        } else {
          this.truncatedCards.delete(index);
        }
      });

      this.cdr.detectChanges();
    }
  }

  // Check if a specific card's text is truncated
  isCardTruncated(index: number): boolean {
    return this.truncatedCards.has(index);
  }

  onSuggestionClick(suggestion: any) {
    this.suggestionSelected.emit(suggestion.title);
  }
}
